/**
 * Makes React's DOM commits survive browser page translation.
 *
 * Chrome/Google Translate and Safari Translate rewrite and move text nodes
 * inside React-owned elements. React then commits against a DOM that no longer
 * matches its tree and the mutation throws NotFoundError — "The node to be
 * removed is not a child of this node" on Chrome, "The object can not be found
 * here" on Safari. That throw happens during commit, so the whole tree comes
 * down and the page is left blank with nothing to navigate back with.
 *
 * The install is permanent and document-wide on purpose: the throw lands while
 * a subtree is being torn down, so a patch a component installs and reverts
 * around itself is already gone by the time it is needed.
 */

const INSTALLED_FLAG = "__decodaTranslationDomSafety";

/** Both browsers report a translated-away node as a NotFoundError DOMException. */
function isMissingNodeError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "NotFoundError") {
    return true;
  }
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("not a child of this node") ||
    error.message.includes("The object can not be found here")
  );
}

export function installTranslationDomSafety(): void {
  if (typeof Node === "undefined") return;

  const proto = Node.prototype as unknown as Record<string, unknown>;
  if (proto[INSTALLED_FLAG]) return;
  proto[INSTALLED_FLAG] = true;

  const originalInsertBefore = Node.prototype.insertBefore;
  const originalRemoveChild = Node.prototype.removeChild;
  const originalReplaceChild = Node.prototype.replaceChild;

  Node.prototype.insertBefore = function <T extends Node>(
    node: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(node);
    }
    try {
      return originalInsertBefore.call(this, node, referenceNode) as T;
    } catch (error) {
      if (isMissingNodeError(error)) return this.appendChild(node);
      throw error;
    }
  };

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    // Translation reparented the node. Detach it where it actually lives so the
    // DOM still converges on what React expects. Recursion terminates: the
    // second call has a matching parent.
    if (child.parentNode && child.parentNode !== this) {
      child.parentNode.removeChild(child);
      return child;
    }
    if (!child.parentNode) return child;
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (isMissingNodeError(error)) return child;
      throw error;
    }
  };

  Node.prototype.replaceChild = function <T extends Node>(
    newChild: Node,
    oldChild: T,
  ): T {
    if (oldChild.parentNode !== this) {
      this.appendChild(newChild);
      return oldChild;
    }
    try {
      return originalReplaceChild.call(this, newChild, oldChild) as T;
    } catch (error) {
      if (!isMissingNodeError(error)) throw error;
      this.appendChild(newChild);
      return oldChild;
    }
  };
}
